import { useState, useEffect, useCallback, useRef } from "react";
import { PLEASE_WAIT, SNACK_BARS_SEVERITY_SUCCESS } from "../constants";

type SnackbarSeverity = "success" | "error" | "warning" | "info";

interface SnackbarOptions {
  autoHide?: boolean;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
  autoHide: boolean;
}

const defaultSnackbarState: SnackbarState = {
  open: false,
  message: "",
  severity: SNACK_BARS_SEVERITY_SUCCESS as SnackbarSeverity,
  autoHide: true,
};

export const useSnackbars = () => {
  const [snackbar, setSnackbar] = useState<SnackbarState>(defaultSnackbarState);
  const lastMessageRef = useRef<string | null>(null);

  const showSnackbar = useCallback(
    (
      message: string,
      severity: SnackbarSeverity = SNACK_BARS_SEVERITY_SUCCESS as SnackbarSeverity,
      options: SnackbarOptions = {},
    ) => {
      // Prevent showing the same message repeatedly (including PLEASE_WAIT)
      if (message === lastMessageRef.current) {
        return;
      }
      lastMessageRef.current = message;

      setSnackbar({
        ...defaultSnackbarState,
        open: true,
        message,
        severity,
        autoHide: options.autoHide ?? defaultSnackbarState.autoHide,
      });
    },
    [],
  );

  const hideSnackbar = useCallback(() => {
    lastMessageRef.current = null;
    setSnackbar(defaultSnackbarState);
  }, []);

  const handleClose = useCallback(
    (event: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === "clickaway") return;

      // Special handling for PLEASE_WAIT messages - don't auto-close
      if (snackbar.message === PLEASE_WAIT && reason === "timeout") {
        return;
      }

      // Reset lastMessageRef when closing so the same message can be shown again later
      lastMessageRef.current = null;
      hideSnackbar();
    },
    [snackbar.message, hideSnackbar],
  );

  // Cleanup effect
  useEffect(() => {
    return () => {
      lastMessageRef.current = null;
      hideSnackbar();
    };
  }, [hideSnackbar]);

  return {
    snackbar,
    showSnackbar,
    hideSnackbar,
    handleClose,
  };
};
